// Plain JavaScript on purpose: next.config.mjs loads this at build time, where
// Node cannot import TypeScript, and app code imports the same file, so the
// rule below exists exactly once. Types live in build-environment.d.mts.

/* global process -- Node at config load, inlined by Next in the browser. */

/**
 * A per-PR preview environment key: a lowercase team prefix and a number,
 * e.g. `gal-4476`, or the ticketless `pr-6702` fallback.
 */
export const PREVIEW_ENV_KEY_PATTERN = /^[a-z][a-z0-9]*-\d+$/;

// Production-looking keys such as `prod-1` fit the preview shape, so they are
// refused by name before the shape is trusted.
const PRODUCTION_SEGMENTS = new Set(['prod', 'production']);

function isPreviewEnvKey(value) {
  if (!PREVIEW_ENV_KEY_PATTERN.test(value)) return false;
  const team = value.slice(0, value.lastIndexOf('-'));
  return !PRODUCTION_SEGMENTS.has(team);
}

/**
 * True for a local `next dev` server, the shared dev deployment and per-PR
 * previews; false for everything else.
 *
 * This is an allowlist that fails closed. The production deploy does not set
 * `NEXT_PUBLIC_DATADOG_ENV` at all, so "not prod" would wrongly include it.
 * The literal `process.env.X` reads below are what lets Next inline the values
 * into client bundles; do not destructure `process.env`.
 */
export function isNonProductionBuild(
  env = {
    NEXT_PUBLIC_DATADOG_ENV: process.env.NEXT_PUBLIC_DATADOG_ENV,
    NODE_ENV: process.env.NODE_ENV,
  },
) {
  if (env.NODE_ENV === 'development') return true;
  const deployment = env.NEXT_PUBLIC_DATADOG_ENV;
  if (deployment === undefined) return false;
  return deployment === 'dev' || isPreviewEnvKey(deployment);
}

const SIGNUP_LOCAL_FAKES_OFF = new Set([undefined, '', '0']);

/**
 * True only for a local `next dev` server whose developer set
 * `NEXT_PUBLIC_SIGNUP_LOCAL_FAKES=1` in an uncommitted `.env.local`. Every
 * built bundle, dev and preview deployments included, is a production build,
 * so it can never select the signup flow's canned local data as the whole
 * implementation. (Dev and preview builds do borrow single fake calls for now,
 * through the temporary gap fill in `signup-api/dev-gap-fill.ts`.)
 *
 * A value other than unset, empty, `0` or `1` throws instead of quietly
 * meaning "off": `next.config.mjs` calls this at load, so a typo fails the
 * server or build at start rather than serving the wrong implementation.
 */
export function isSignupLocalFakesBuild(
  env = {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_SIGNUP_LOCAL_FAKES: process.env.NEXT_PUBLIC_SIGNUP_LOCAL_FAKES,
  },
) {
  const value = env.NEXT_PUBLIC_SIGNUP_LOCAL_FAKES;
  if (!SIGNUP_LOCAL_FAKES_OFF.has(value) && value !== '1') {
    throw new Error(
      `NEXT_PUBLIC_SIGNUP_LOCAL_FAKES must be unset, empty, 0 or 1 (got ${JSON.stringify(value)})`,
    );
  }
  return env.NODE_ENV === 'development' && value === '1';
}

/**
 * The signup Book a call page's Member Support calendar link, as built into
 * the bundle, or undefined when the build sets none. The signup feature
 * validates it; this only reads it, as a literal property so Next inlines it.
 */
export function signupBookCallCalendarUrlSetting(
  env = {
    NEXT_PUBLIC_SIGNUP_BOOK_CALL_CALENDAR_URL:
      process.env.NEXT_PUBLIC_SIGNUP_BOOK_CALL_CALENDAR_URL,
  },
) {
  return env.NEXT_PUBLIC_SIGNUP_BOOK_CALL_CALENDAR_URL;
}
